<template>
  <SignOutDialog v-if="showSignOut" @closeDialog="handleDialogClose(false)" @signout="handleDialogClose(true)" />
  <CompanyChangeDialog v-if="showCompanyChange" @closeDialog="handleDialogClose" @companyChanged="handleDialogClose" />
  <v-app-bar elevation="0" :color="mainColour" :height="appBarHeight">
    <v-app-bar-nav-icon @click="$emit('toggleSidebar')"></v-app-bar-nav-icon>
    <div class="d-flex align-center ml-1" style="min-width: 0; flex: 1 1 auto; gap: 8px;">
      <template v-for="(item, index) in props.companyInfo.divisionPath" :key="item.id">
        <!-- Root Company (Index 0) -->
        <template v-if="index === 0">
          <v-btn v-if="props.companyInfo.hasMultiple" variant="text" color="white" class="text-capitalize pa-0 ma-0"
            @click="showCompanyChange = true" style="min-width: 0; display: flex; align-items: center;">
            <div style="flex: 1 1 auto; min-width: 0;">
              <span :class="mobile ? 'text-xsmall text-truncate' : 'text-truncate'">{{ item.name }}</span>
              <div v-if="item.parentName" class="text-caption text-white text-opacity-80"
                style="font-size: 0.75rem; line-height: 1.2;">
                A division of {{ item.parentName }}
              </div>
            </div>
            <v-icon size="small" icon="mdi-menu-down-outline" class="ml-1 flex-shrink-0"></v-icon>
          </v-btn>
          <div v-else style="flex: 1 1 auto; min-width: 0;">
            <span :class="mobile ? 'text-xsmall text-truncate' : 'text-truncate'">{{ item.name }}</span>
            <div v-if="item.parentName" class="text-caption text-white text-opacity-80"
              style="font-size: 0.75rem; line-height: 1.2;">
              A division of {{ item.parentName }}
            </div>
          </div>
          <div v-if="hasDivisionLabels() && !mobile" style="flex: 1 1 140px; max-width: 180px;">
            <v-select v-model="selectedDivisionLabel" :items="divisionLabelOptions()" item-title="name" item-value="id"
              density="compact" variant="outlined" hide-details :label="'Divisions'" class="mt-2 division-select"
              @update:model-value="onDivisionChange">
            </v-select>
          </div>
          <div v-if="hasGroups && !mobile" style="flex: 1 1 140px; max-width: 180px;">
            <v-select v-model="selectedGroupLabel" :items="groupLabelOptions" item-title="name" item-value="id"
              density="compact" variant="outlined" hide-details :label="'Groups'" class="mt-2 division-select"
              @update:model-value="onGroupChange">
            </v-select>
          </div>

          <!-- Mobile: Combined settings & account menu -->
          <v-menu v-if="mobile" v-model="showMobileMenu" location="bottom" :close-on-content-click="false">
            <template v-slot:activator="{ props: menuProps }">
              <v-btn v-bind="menuProps" icon variant="text" color="white" size="small" title="Menu">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-card min-width="260">
              <v-list-item class="py-3">
                <v-list-item-title class="text-subtitle-1 font-weight-medium">Hi, {{ userInfo.firstName }}!</v-list-item-title>
              </v-list-item>

              <div v-if="hasDivisionLabels() || hasGroups" class="pa-3 d-flex flex-column" style="gap: 12px;">
                <div v-if="hasDivisionLabels()">
                  <v-select v-model="selectedDivisionLabel" :items="divisionLabelOptions()" item-title="name" item-value="id"
                    density="compact" variant="outlined" hide-details :label="'Divisions'" class="division-select"
                    @update:model-value="onDivisionChange">
                  </v-select>
                </div>
                <div v-if="hasGroups">
                  <v-select v-model="selectedGroupLabel" :items="groupLabelOptions" item-title="name" item-value="id"
                    density="compact" variant="outlined" hide-details :label="'Groups'" class="division-select"
                    @update:model-value="onGroupChange">
                  </v-select>
                </div>
              </div>

              <v-list density="compact">
                <v-list-item prepend-icon="mdi-arrow-left-bold-box-outline" @click="showMobileMenu = false; toggleV2(company)">
                  <v-list-item-title>Back to v2</v-list-item-title>
                </v-list-item>
                <v-list-item v-for="item in profileMenuItems" :key="item.value"
                  :prepend-icon="item.icon" @click="showMobileMenu = false; handleMenuClick(item.value)">
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </template>
      </template>
    </div>

    <v-spacer></v-spacer>

    <v-tooltip text="AI Assistant" location="bottom" v-if="enableAiChat">
      <template v-slot:activator="{ props: tooltipProps }">
        <v-btn v-bind="tooltipProps" variant="tonal" color="white" size="small" @click="setSidebarOpen(true)"
          :icon="mobile" class="mx-2">
          <v-icon :class="{ 'mr-1': !mobile }">mdi-robot-outline</v-icon>
          <span v-if="!mobile">Ask Axle&#8482; AI</span>
        </v-btn>
      </template>
    </v-tooltip>

    <v-tooltip v-if="!mobile" text="Back to Version 2" location="bottom">
      <template v-slot:activator="{ props: tooltipProps }">
        <v-btn v-bind="tooltipProps" variant="tonal" color="white" size="small" @click="toggleV2(company)"
          class="mx-2">
          <v-icon class="mr-1">mdi-arrow-left-bold-box-outline</v-icon>
          <span>Back to v2</span>
        </v-btn>
      </template>
    </v-tooltip>

    <div v-if="!mobile" class="d-flex align-center mr-2">
      <span class="text-body-2 mr-2">Hi, {{ userInfo.firstName }}!</span>

      <v-menu location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props">
            <img v-if="isDecember" src="@/v3/assets/2025/SantaHatProfile1.png" width="35" />
            <v-icon v-else size="x-large">mdi-account-outline</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item v-for="item in profileMenuItems" :key="item.value" @click="handleMenuClick(item.value)"
            :prepend-icon="item.icon">
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-app-bar>
</template>

<script setup>
import { useProfile } from '@/v3/composables/useProfile';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay, useTheme } from 'vuetify';
import { useStore } from 'vuex';
import { useConnection } from '../composables/useConnection';

// import Company from "@/domain/model/company/Company.js";
import RcTime from '@/domain/session/time/RcTime.js';

import ConstUtils from '@/utils/ConstUtils.js';
import ContentUtils from '@/utils/ContentUtils';
import { useAiContext } from '@/v3/composables/useAiContext';
import CompanyChangeDialog from "@/v3/pages/operator/profile/dialogs/CompanyChangeDialog.vue";
import SignOutDialog from "@/v3/pages/operator/profile/dialogs/SignOutDialog.vue";

const route = useRoute();
const store = useStore();
const domain = computed(() => store.getters.domain);
const companyId = computed(() => ContentUtils.baseDecode(route.params.operatorId));
const { log } = useConnection();

const props = defineProps({
  companyInfo: { type: Object, default: () => ({}) },
  divisions: { type: Array, default: () => ([]) },
  userInfo: { type: Object, default: () => ({ firstName: 'Guest', idEncoded: null }) },
});

const emit = defineEmits(['toggleSidebar', 'navigate', 'divisionChanged', 'groupChanged']);

const theme = useTheme();
const { mobile } = useDisplay();
const router = useRouter();
const showSignOut = ref(false);
const showCompanyChange = ref(false);
const showMobileMenu = ref(false);
const { setSidebarOpen } = useAiContext();
// Axle is available to every signed-in user; the Ask Axle button is no longer gated by a stored flag.
const enableAiChat = computed(() => true);

const company = ref(null);

const profileMenuItems = ref([
  { title: 'Profile', icon: 'mdi-account-circle-outline', value: 'profile', requiresRole: 'default', version: 'v3' },
  { title: 'Sign Out', icon: 'mdi-logout', value: 'signOut', requiresRole: 'default', version: '' },
]);

const mainColour = computed(() => {
  var colour = theme.global.name.value === 'dark' ? 'grey-darken-4' : 'grey-lighten-4';
  colour = 'primary';
  return colour;
});

const appBarHeight = computed(() => {
  // if (mobile.value) return 120;
  return 64;
});

const isDecember = computed(() => {
  let rcTime = RcTime.time();
  let now = rcTime.now();
  return now.month().value() == 11;
});

const handleMenuClick = (action) => {
  if (action === 'profile') {
    const path = { value: 'profile', version: 'v3', content: '' };
    if (domain.value.session().employee().find().roles().find().isAdministrator()) {
      path['content'] = 'admin';
    }
    emit('navigate', path);
  } else if (action === 'signOut') {
    showSignOut.value = true;
  }
};

const handleDialogClose = (signOut = false) => {
  showSignOut.value = false;
  showCompanyChange.value = false;
  if (signOut) {
    router.replace({ name: ConstUtils.ROUTES.SIGNIN });
  }
};

const hasDivisionLabels = () => {
  return divisionLabelOptions().length > 0;
};

const selectedDivisionLabel = ref('all');
const divisionLabelOptions = () => {
  let list = [];
  let labelList = [];
  let c = domain.value.session().company();
  const labelMap = c.labels();
  const sorted = labelMap.sort();
  for (var index = 0; index < sorted.length; index++) {
    let label = sorted[index];
    if (label.isTypeGroup()) {
      continue;
    }
    let labelData = label.data;
    labelList.push({ id: labelData['@rid'], name: labelData['name'] });
  }
  for (let index in labelList) {
    let location = labelList[index];
    list.push({ id: location['id'], name: location['name'] });
  }

  if (labelList.length > 0) {
    list.unshift({ id: 'none', name: 'None' });
    list.unshift({ id: 'all', name: 'All' });
  }

  return list;
};

const hasGroups = computed(() => {
  return groupLabelOptions.value.length > 0;
});
const selectedGroupLabel = ref('all');
const groupLabelOptions = computed(() => {
  let list = [];
  let labelList = [];
  let c = domain.value.session().company();
  const labelMap = c.labels();
  const sorted = labelMap.sort();
  for (var index = 0; index < sorted.length; index++) {
    let label = sorted[index];
    if (label.isTypeDivision()) {
      continue;
    }
    let labelData = label.data;
    labelList.push({ id: labelData['@rid'], name: labelData['name'] });
  }
  for (let index in labelList) {
    let location = labelList[index];
    list.push({ id: location['id'], name: location['name'] });
  }
  if (labelList.length > 0) {
    list.unshift({ id: 'none', name: 'None' });
    list.unshift({ id: 'all', name: 'All' });
  }
  return list;
});

const onDivisionChange = (value) => {
  selectedGroupLabel.value = 'all';
  emit('divisionChanged', value);
};

const onGroupChange = (value) => {
  emit('groupChanged', value);
};

const { toggleV2 } = useProfile(domain.value);

onMounted(() => {
  company.value = domain.value.companys().findById(companyId.value);
  log.info("Started AppHeader v3");
});
</script>

<style scoped>
.division-select :deep(.v-field__input) {
  font-size: 0.85rem;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  min-height: 32px !important;
}

.division-select :deep(.v-field) {
  border-radius: 8px !important;
}

@media (max-width: 600px) {
  .division-select :deep(.v-field__input) {
    font-size: 0.75rem;
    padding-left: 6px !important;
    padding-right: 6px !important;
  }
}
</style>
